import React, { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type {
  AmcVisit,
  AmcVisitAssignmentSummary,
} from "features/AmcVisitLogs/types/amc-visit.types";

function assigneeLabel(a: AmcVisitAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: AmcVisitAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dedupeKey(a: AmcVisitAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function AmcVisitAssigneesAvatarGroup({
  visit,
  assignments,
  visitId,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  visit: AmcVisit;
  assignments?: AmcVisitAssignmentSummary[] | null;
  visitId: number;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (v: AmcVisit) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = (assignments ?? []).filter(
      (a) => !(a as any).deleted_at,
    );
    const map = new Map<number, AmcVisitAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => {
      const emp = a.user?.employee;
      return {
        key: dedupeKey(a) || a.id,
        userId: assigneeUserId(a),
        label: assigneeLabel(a),
        avatar: a.user?.avatar,
        employee:
          emp != null ? { id: emp.id, code: emp.code ?? "" } : undefined,
      };
    });
  }, [assignments]);

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={`/amc-management/visit-logs/${visitId}`}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(visit)}
      emptyTitle="No assignees — open record to assign"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
